/**
 * Orders List Page with Statistics
 */
import { useState, useMemo, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Skeleton,
  Tabs,
  Tab,
  ListSubheader,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  ShoppingCart as OrdersIcon,
  AttachMoney as RevenueIcon,
  AttachMoney as AttachMoneyIcon,
  TrendingUp as TrendingUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  ClearAll as ClearAllIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import {
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { PageHeader, DataTable, StatusChip, Column, StatsCard } from '../../components/common';
import { useGetOrdersQuery, useGetOrderStatisticsQuery, useUpdateOrderStatusMutation } from '../../store/api/ordersApi';
import { OrderListItem, OrderStatus, ORDER_STATUS_CONFIG } from '../../types/order';
import {
  getAllowedNextOrderStatuses,
  splitAllowedStatusesForSelect,
  formatOrderStatusUpdateError,
  orderTransitionRequiresPlatformFee,
  normalizePlatformFeeInput,
} from '../../utils/orderStatusTransitions';
import { palette } from '../../theme/palette';
import { usePermissions } from '../../hooks/usePermissions';
import moment from 'moment';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useLazyGetOrdersDetailsReportQuery } from '../../store/api/detailedReportsApi';
import { downloadCsv } from '../../utils/csv';
import { orderExportColumns } from '../../utils/orderExport';
import {
  formatNumber as formatNumberLocalized,
  formatCurrency as formatCurrencyLocalized,
} from '../../utils/format';

/** Clickable status (chip + chevron); opens menu — same flow as order detail after picking a status. */
function OrderListStatusTrigger({
  row,
  canUpdate,
  updating,
  onOpenStatusMenu,
}: {
  row: OrderListItem;
  canUpdate: boolean;
  updating: boolean;
  onOpenStatusMenu: (event: MouseEvent<HTMLElement>, row: OrderListItem) => void;
}) {
  const { t } = useTranslation();
  const allowed = getAllowedNextOrderStatuses(row.status);

  if (!canUpdate || allowed.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
        <StatusChip status={row.status} />
      </Box>
    );
  }

  return (
    <Box
      component="button"
      type="button"
      disabled={updating}
      onClick={(e) => {
        e.stopPropagation();
        onOpenStatusMenu(e, row);
      }}
      aria-label={t('serviceOrders.buttons.changeStatus')}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.25,
        cursor: updating ? 'wait' : 'pointer',
        border: 'none',
        background: 'none',
        padding: 0,
        margin: 0,
        font: 'inherit',
        color: 'inherit',
        minHeight: 40,
        borderRadius: 1,
        '&:hover:not(:disabled)': { opacity: 0.85 },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 2,
        },
      }}
    >
      {updating ? (
        <CircularProgress size={20} thickness={4} />
      ) : (
        <>
          <StatusChip status={row.status} />
          <KeyboardArrowDownIcon sx={{ fontSize: '1.125rem', color: 'text.secondary', flexShrink: 0 }} />
        </>
      )}
    </Box>
  );
}

const OrdersListPage = ({ embedded = false }: { embedded?: boolean } = {}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [status, setStatus] = useState<string>('');
  const [search, setSearch] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [specialRequest, setSpecialRequest] = useState<string>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const canExportOrders = hasPermission('export_orders');
  const [fetchExport, { isFetching: isExporting }] = useLazyGetOrdersDetailsReportQuery();
  const [updatingOrderId, setUpdatingOrderId] = useState<number | null>(null);

  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
  const [statusMenuRow, setStatusMenuRow] = useState<OrderListItem | null>(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusDialogRow, setStatusDialogRow] = useState<OrderListItem | null>(null);
  const [statusNewStatus, setStatusNewStatus] = useState<OrderStatus | ''>('');
  const [statusAdminNotes, setStatusAdminNotes] = useState('');
  const [statusDialogPlatformFee, setStatusDialogPlatformFee] = useState('0.00');

  const [updateOrderStatus, { isLoading: isStatusUpdating }] = useUpdateOrderStatusMutation();

  const { data, isLoading, error } = useGetOrdersQuery(
    {
      page: page + 1,
      page_size: rowsPerPage,
      status: status as OrderStatus || undefined,
      search: search || undefined,
      special_request:
        specialRequest === 'true' ? true : specialRequest === 'false' ? false : undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
    },
    {
      pollingInterval: 10000,
      skipPollingIfUnfocused: true,
      refetchOnMountOrArgChange: true,
    }
  );

  const { data: statsData, isLoading: statsLoading } = useGetOrderStatisticsQuery(undefined, {
    pollingInterval: 10000,
    skipPollingIfUnfocused: true,
    refetchOnMountOrArgChange: true,
  });

  // Format number (locale-aware)
  const formatNumber = (num: number): string => formatNumberLocalized(num);

  // Format currency (locale-aware)
  const formatCurrency = (amount: number): string => formatCurrencyLocalized(amount);

  // Check if user can view order details
  const canViewOrderDetails = hasPermission('view_order_details');
  /** Backend allows update_order_status only (not edit_orders — use patient/location PATCH for that). */
  const canUpdateOrderStatus = hasPermission('update_order_status');
  const canEditOrders = hasPermission('edit_orders');

  const closeStatusMenu = () => {
    setStatusMenuAnchor(null);
    setStatusMenuRow(null);
  };

  const openStatusMenu = (event: MouseEvent<HTMLElement>, row: OrderListItem) => {
    setStatusMenuAnchor(event.currentTarget);
    setStatusMenuRow(row);
  };

  const closeStatusDialog = () => {
    setStatusDialogOpen(false);
    setStatusDialogRow(null);
    setStatusNewStatus('');
    setStatusAdminNotes('');
    setStatusDialogPlatformFee('0.00');
  };

  const statusMenuSplit = useMemo(() => {
    if (!statusMenuRow) {
      return { forward: [] as OrderStatus[], terminal: [] as OrderStatus[] };
    }
    return splitAllowedStatusesForSelect(getAllowedNextOrderStatuses(statusMenuRow.status));
  }, [statusMenuRow]);

  const statusDialogSplit = useMemo(() => {
    if (!statusDialogRow) {
      return { forward: [] as OrderStatus[], terminal: [] as OrderStatus[] };
    }
    return splitAllowedStatusesForSelect(getAllowedNextOrderStatuses(statusDialogRow.status));
  }, [statusDialogRow]);

  const handleStatusMenuPick = (picked: OrderStatus) => {
    if (!statusMenuRow) return;
    setStatusDialogRow(statusMenuRow);
    setStatusNewStatus(picked);
    setStatusAdminNotes('');
    setStatusDialogPlatformFee('0.00');
    closeStatusMenu();
    setStatusDialogOpen(true);
  };

  const handleStatusDialogSubmit = async () => {
    if (!statusDialogRow || !statusNewStatus) return;

    let platform_fee: string | undefined;
    if (orderTransitionRequiresPlatformFee(statusDialogRow.status, statusNewStatus)) {
      const normalized = normalizePlatformFeeInput(statusDialogPlatformFee);
      if (!normalized) {
        toast.error(t('serviceOrders.toast.invalidDeliveryFee'));
        return;
      }
      platform_fee = normalized;
    }

    setUpdatingOrderId(statusDialogRow.id);
    try {
      await updateOrderStatus({
        order_id: statusDialogRow.id,
        status: statusNewStatus,
        admin_notes: statusAdminNotes || undefined,
        ...(platform_fee !== undefined && { platform_fee }),
      }).unwrap();
      toast.success(t('serviceOrders.toast.orderStatusUpdated', { order: statusDialogRow.order_number }));
      closeStatusDialog();
    } catch (err) {
      toast.error(formatOrderStatusUpdateError(err));
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const statusListConfirmRequiresFee = Boolean(
    statusDialogRow &&
      statusNewStatus &&
      orderTransitionRequiresPlatformFee(statusDialogRow.status, statusNewStatus)
  );

  // Prepare payment methods data
  const paymentMethodsData = useMemo(() => {
    if (!statsData?.data?.payment_methods) return [];
    const methods = statsData.data.payment_methods;
    return [
      { name: t('serviceOrders.payment.cash'), value: methods.CASH, color: palette.status.success },
      { name: t('serviceOrders.payment.fawry'), value: methods.FAWRY, color: palette.brand[500] },
      { name: t('serviceOrders.payment.creditCard'), value: methods.CREDIT_CARD, color: palette.brandSecondary[500] },
    ].filter((item) => item.value > 0);
  }, [statsData, t]);

  const columns: Column<OrderListItem>[] = [
    {
      id: 'order_number',
      label: t('serviceOrders.cols.orderNumber'),
      minWidth: 200,
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {canViewOrderDetails ? (
            // Clickable order number for users with view_order_details permission
            <Box
              onClick={() => navigate(`/orders/${row.id}`)}
              sx={{
                fontWeight: 600,
                color: 'primary.main',
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                  color: 'primary.dark',
                },
              }}
            >
              {row.order_number}
            </Box>
          ) : (
            // Plain text for users without view_order_details permission
            <Box sx={{ fontWeight: 600, color: 'text.primary' }}>
              {row.order_number}
            </Box>
          )}
          
        </Box>
      ),
    },
    {
      id: 'patient_name',
      label: t('serviceOrders.cols.patient'),
      minWidth: 150,
    },
    {
      id: 'service_name',
      label: t('serviceOrders.cols.service'),
      minWidth: 150,
    },
    {
      id: 'subservice_name',
      label: t('serviceOrders.cols.subservice'),
      minWidth: 150,
    },
    {
      id: 'scheduled_date',
      label: t('serviceOrders.cols.scheduled'),
      minWidth: 120,
      render: (row) => moment(row.scheduled_date).format('MMM DD, YYYY'),
    },
    {
      id: 'total_amount',
      label: t('serviceOrders.cols.amount'),
      minWidth: 100,
      align: 'right',
      render: (row) => formatCurrency(parseFloat(row.total_amount)),
    },
    {
      id: 'status',
      label: t('serviceOrders.cols.status'),
      minWidth: 160,
      render: (row) => (
        <OrderListStatusTrigger
          row={row}
          canUpdate={canUpdateOrderStatus}
          updating={updatingOrderId === row.id}
          onOpenStatusMenu={openStatusMenu}
        />
      ),
    },
    {
      id: 'created_at',
      label: t('serviceOrders.cols.created'),
      minWidth: 120,
      render: (row) => moment(row.created_at).format('MMM DD, YYYY'),
    },
    ...(canEditOrders
      ? [
          {
            id: 'actions',
            label: t('common.actions'),
            minWidth: 72,
            align: 'right' as const,
            render: (row: OrderListItem) => (
              <Tooltip title={t('serviceOrders.buttons.editOrder')}>
                <IconButton
                  size="small"
                  color="primary"
                  aria-label={t('serviceOrders.buttons.editOrder')}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/orders/${row.id}`);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ),
          },
        ]
      : []),
  ];

  const stats = statsData?.data;

  const hasActiveOrderFilters = Boolean(status || search || specialRequest || dateFrom || dateTo);

  const handleResetOrderFilters = () => {
    setStatus('');
    setSearch('');
    setSpecialRequest('');
    setDateFrom('');
    setDateTo('');
    setPage(0);
  };

  // Excel export of exactly what the current filters show (all pages)
  const handleExportOrders = async () => {
    try {
      const result = await fetchExport({
        status: status || undefined,
        search: search || undefined,
        special_request: specialRequest === 'true' ? true : specialRequest === 'false' ? false : undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      }).unwrap();
      downloadCsv(orderExportColumns(t), result.data.rows, `orders_${dateFrom || 'all'}_${dateTo || 'all'}`);
    } catch {
      toast.error(t('reports.toast.exportError'));
    }
  };

  return (
    <Box sx={{ p: embedded ? 0 : 3 }}>
      {!embedded && (
        <PageHeader
          title={t('serviceOrders.title')}
          subtitle={t('serviceOrders.subtitle')}
          breadcrumbs={[
            { label: t('nav.dashboard'), path: '/' },
            { label: t('nav.orders') },
          ]}
        />
      )}

      {/* Statistics Section */}
      {statsLoading ? (
        <Box sx={{ mb: 3 }}>
          <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
        </Box>
      ) : stats ? (
        <Box sx={{ mb: 3 }}>
          <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
            <Tab label={t('serviceOrders.tabs.overview')} />
            <Tab label={t('serviceOrders.tabs.statistics')} />
            <Tab label={t('serviceOrders.tabs.topPerformers')} />
          </Tabs>

          {/* Overview Tab */}
          {tabValue === 0 && (
            <Box>
              {/* Overview Stats Cards */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(4, 1fr)',
                  },
                  gap: 2,
                  mb: 3,
                }}
              >
                <StatsCard
                  title={t('serviceOrders.stats.totalOrders')}
                  value={formatNumber(stats.overview.total_orders)}
                  icon={<OrdersIcon />}
                  color="primary"
                />
                <StatsCard
                  title={t('serviceOrders.stats.ordersToday')}
                  value={formatNumber(stats.overview.orders_today)}
                  icon={<TrendingUpIcon />}
                  color="info"
                />
                <StatsCard
                  title={t('serviceOrders.stats.totalRevenue')}
                  value={formatCurrency(stats.revenue.total_revenue)}
                  icon={<RevenueIcon />}
                  color="success"
                />
                <StatsCard
                  title={t('serviceOrders.stats.avgOrderValue')}
                  value={formatCurrency(stats.revenue.average_order_value)}
                  icon={<AttachMoneyIcon />}
                  color="secondary"
                />
              </Box>
            </Box>
          )}

          {/* Statistics Tab */}
          {tabValue === 1 && (
            <Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    lg: 'repeat(2, 1fr)',
                  },
                  gap: 3,
                  mb: 3,
                }}
              >
                {/* Payment Methods Chart */}
                <Card>
                  <CardContent>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {t('serviceOrders.stats.paymentMethods')}
                    </Typography>
                    <Box sx={{ height: 300 }}>
                      {paymentMethodsData.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={paymentMethodsData}
                              cx="50%"
                              cy="50%"
                              innerRadius={60}
                              outerRadius={100}
                              dataKey="value"
                              label={({ name, value, percent }) => {
                                if (value === 0) return '';
                                return `${name} ${((percent ?? 0) * 100).toFixed(0)}%`;
                              }}
                            >
                              {paymentMethodsData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <RechartsTooltip />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <Box
                          sx={{
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'text.secondary',
                          }}
                        >
                          <Typography>{t('serviceOrders.empty.paymentData')}</Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>

                {/* Order Status Breakdown Table */}
                <Card>
                  <CardContent>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {t('serviceOrders.stats.ordersByStatus')}
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableBody>
                          {Object.entries(stats.by_status).map(([statusKey, count]) => (
                            <TableRow key={statusKey}>
                              <TableCell>
                                <StatusChip status={statusKey as OrderStatus} />
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body1" fontWeight={600}>
                                  {formatNumber(count)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Box>

              {/* Provider Breakdown */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {t('serviceOrders.stats.providerBreakdown')}
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(3, 1fr)',
                      },
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {t('serviceOrders.stats.withSpecialist')}
                      </Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {formatNumber(stats.by_provider.with_specialist)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {t('serviceOrders.stats.withConsultant')}
                      </Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {formatNumber(stats.by_provider.with_consultant)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {t('serviceOrders.stats.unassigned')}
                      </Typography>
                      <Typography variant="h5" fontWeight={700} color="warning.main">
                        {formatNumber(stats.by_provider.unassigned)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Payment Methods Breakdown */}
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {t('serviceOrders.stats.paymentMethodsBreakdown')}
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(3, 1fr)',
                      },
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {t('serviceOrders.payment.cash')}
                      </Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {formatNumber(stats.payment_methods.CASH)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {t('serviceOrders.payment.fawry')}
                      </Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {formatNumber(stats.payment_methods.FAWRY)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {t('serviceOrders.payment.creditCard')}
                      </Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {formatNumber(stats.payment_methods.CREDIT_CARD)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Top Performers Tab */}
          {tabValue === 2 && (
            <Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    lg: 'repeat(2, 1fr)',
                  },
                  gap: 3,
                  mb: 3,
                }}
              >
                {/* Top Services */}
                <Card>
                  <CardContent>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {t('serviceOrders.stats.topServices')}
                    </Typography>
                    {stats.top_services.length > 0 ? (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>{t('serviceOrders.cols.service')}</TableCell>
                              <TableCell align="right">{t('serviceOrders.cols.orders')}</TableCell>
                              <TableCell align="right">{t('serviceOrders.cols.revenue')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {stats.top_services.map((service) => (
                              <TableRow key={service.id} hover>
                                <TableCell>
                                  <Typography variant="body2" fontWeight={500}>
                                    {service.name}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Chip label={formatNumber(service.order_count)} size="small" />
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" fontWeight={600}>
                                    {formatCurrency(service.total_revenue)}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography color="text.secondary" sx={{ py: 2 }}>
                        {t('serviceOrders.empty.serviceData')}
                      </Typography>
                    )}
                  </CardContent>
                </Card>

                {/* Top Subservices */}
                <Card>
                  <CardContent>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {t('serviceOrders.stats.topSubservices')}
                    </Typography>
                    {stats.top_subservices.length > 0 ? (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>{t('serviceOrders.cols.subservice')}</TableCell>
                              <TableCell align="right">{t('serviceOrders.cols.orders')}</TableCell>
                              <TableCell align="right">{t('serviceOrders.cols.revenue')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {stats.top_subservices.map((subservice) => (
                              <TableRow key={subservice.id} hover>
                                <TableCell>
                                  <Typography variant="body2" fontWeight={500}>
                                    {subservice.name}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Chip label={formatNumber(subservice.order_count)} size="small" />
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" fontWeight={600}>
                                    {formatCurrency(subservice.total_revenue)}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography color="text.secondary" sx={{ py: 2 }}>
                        {t('serviceOrders.empty.subserviceData')}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    lg: 'repeat(2, 1fr)',
                  },
                  gap: 3,
                }}
              >
                {/* Top Specialists */}
                <Card>
                  <CardContent>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {t('serviceOrders.stats.topSpecialists')}
                    </Typography>
                    {stats.top_specialists.length > 0 ? (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>{t('serviceOrders.cols.specialist')}</TableCell>
                              <TableCell align="right">{t('serviceOrders.cols.orders')}</TableCell>
                              <TableCell align="right">{t('serviceOrders.cols.completed')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {stats.top_specialists.map((specialist) => (
                              <TableRow key={specialist.id} hover>
                                <TableCell>
                                  <Typography variant="body2" fontWeight={500}>
                                    {specialist.name}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Chip label={formatNumber(specialist.order_count)} size="small" />
                                </TableCell>
                                <TableCell align="right">
                                  <Chip
                                    label={formatNumber(specialist.completed_orders)}
                                    size="small"
                                    color="success"
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography color="text.secondary" sx={{ py: 2 }}>
                        {t('serviceOrders.empty.specialistData')}
                      </Typography>
                    )}
                  </CardContent>
                </Card>

                {/* Top Consultants */}
                <Card>
                  <CardContent>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {t('serviceOrders.stats.topConsultants')}
                    </Typography>
                    {stats.top_consultants.length > 0 ? (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>{t('serviceOrders.cols.consultant')}</TableCell>
                              <TableCell align="right">{t('serviceOrders.cols.orders')}</TableCell>
                              <TableCell align="right">{t('serviceOrders.cols.completed')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {stats.top_consultants.map((consultant) => (
                              <TableRow key={consultant.id} hover>
                                <TableCell>
                                  <Typography variant="body2" fontWeight={500}>
                                    {consultant.name}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Chip label={formatNumber(consultant.order_count)} size="small" />
                                </TableCell>
                                <TableCell align="right">
                                  <Chip
                                    label={formatNumber(consultant.completed_orders)}
                                    size="small"
                                    color="success"
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography color="text.secondary" sx={{ py: 2 }}>
                        {t('serviceOrders.empty.consultantData')}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Box>
            </Box>
          )}
        </Box>
      ) : null}

      {/* Filters */}
      <Card sx={{ p: 2, mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <TextField
            placeholder={t('serviceOrders.filters.searchPlaceholder')}
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('serviceOrders.filters.status')}</InputLabel>
            <Select
              value={status}
              label={t('serviceOrders.filters.status')}
              onChange={(e) => setStatus(e.target.value)}
            >
              <MenuItem value="">{t('serviceOrders.filters.allStatuses')}</MenuItem>
              {Object.keys(ORDER_STATUS_CONFIG).map((key) => (
                <MenuItem key={key} value={key}>
                  {t(`serviceOrders.status.${key}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>{t('serviceOrders.filters.requestType')}</InputLabel>
            <Select
              value={specialRequest}
              label={t('serviceOrders.filters.requestType')}
              onChange={(e) => setSpecialRequest(e.target.value)}
            >
              <MenuItem value="">{t('serviceOrders.filters.allOrders')}</MenuItem>
              <MenuItem value="true">{t('serviceOrders.filters.specialRequests')}</MenuItem>
              <MenuItem value="false">{t('serviceOrders.filters.regularOrders')}</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            type="date"
            label={t('reports.filters.dateFrom')}
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPage(0);
            }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            type="date"
            label={t('reports.filters.dateTo')}
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPage(0);
            }}
            InputLabelProps={{ shrink: true }}
          />
          {canExportOrders && (
            <Button
              variant="outlined"
              size="small"
              startIcon={isExporting ? <CircularProgress size={16} /> : <DownloadIcon />}
              onClick={handleExportOrders}
              disabled={isExporting}
            >
              {t('reports.detailed.exportExcel')}
            </Button>
          )}
          {hasActiveOrderFilters && (
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              startIcon={<ClearAllIcon />}
              onClick={handleResetOrderFilters}
              sx={{ ml: { xs: 0, sm: 'auto' } }}
            >
              {t('serviceOrders.filters.reset')}
            </Button>
          )}
        </Box>
      </Card>

      {/* Orders Table */}
      <DataTable
        columns={columns}
        data={data?.results || []}
        loading={isLoading}
        error={error ? t('serviceOrders.errors.loadFailed') : null}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={data?.count || 0}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        emptyMessage={t('serviceOrders.empty.orders')}
      />

      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor && statusMenuRow)}
        onClose={closeStatusMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          elevation: 8,
          sx: { minWidth: 200, maxHeight: 360, mt: 0.5 },
        }}
      >
        {statusMenuSplit.forward.length > 0 && (
          <ListSubheader sx={{ lineHeight: '32px', fontWeight: 600 }}>
            {statusMenuSplit.terminal.length > 0 ? t('serviceOrders.dialog.moveForward') : t('serviceOrders.dialog.nextStatus')}
          </ListSubheader>
        )}
        {statusMenuSplit.forward.map((key) => (
          <MenuItem
            key={key}
            onClick={() => handleStatusMenuPick(key)}
            dense
          >
            {t(`serviceOrders.status.${key}`)}
          </MenuItem>
        ))}
        {statusMenuSplit.terminal.length > 0 && (
          <ListSubheader sx={{ lineHeight: '32px', fontWeight: 600 }}>{t('serviceOrders.dialog.cancelOrReject')}</ListSubheader>
        )}
        {statusMenuSplit.terminal.map((key) => (
          <MenuItem key={key} onClick={() => handleStatusMenuPick(key)} dense sx={{ color: 'error.main' }}>
            {t(`serviceOrders.status.${key}`)}
          </MenuItem>
        ))}
      </Menu>

      <Dialog open={statusDialogOpen} onClose={closeStatusDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('serviceOrders.dialog.updateStatusTitle')}</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {statusDialogRow && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('serviceOrders.dialog.currentStatus')} <strong>{t(`serviceOrders.status.${statusDialogRow.status}`)}</strong>. {t('serviceOrders.dialog.forwardOnlyNote')}
            </Typography>
          )}
          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>{t('serviceOrders.dialog.newStatus')}</InputLabel>
            <Select
              value={statusNewStatus}
              label={t('serviceOrders.dialog.newStatus')}
              onChange={(e) => setStatusNewStatus(e.target.value as OrderStatus)}
              MenuProps={{ PaperProps: { style: { maxHeight: 360 } } }}
            >
              {statusDialogSplit.forward.length > 0 && (
                <ListSubheader sx={{ lineHeight: '32px', fontWeight: 600 }}>
                  {statusDialogSplit.terminal.length > 0 ? t('serviceOrders.dialog.moveForward') : t('serviceOrders.dialog.nextStatus')}
                </ListSubheader>
              )}
              {statusDialogSplit.forward.map((key) => (
                <MenuItem key={key} value={key}>
                  {t(`serviceOrders.status.${key}`)}
                </MenuItem>
              ))}
              {statusDialogSplit.terminal.length > 0 && (
                <ListSubheader sx={{ lineHeight: '32px', fontWeight: 600 }}>{t('serviceOrders.dialog.cancelOrReject')}</ListSubheader>
              )}
              {statusDialogSplit.terminal.map((key) => (
                <MenuItem key={key} value={key}>
                  {t(`serviceOrders.status.${key}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {statusListConfirmRequiresFee && (
            <TextField
              fullWidth
              sx={{ mb: 2 }}
              label={t('serviceOrders.dialog.deliveryFeeLabel')}
              type="number"
              inputProps={{ min: 0, step: '0.01' }}
              value={statusDialogPlatformFee}
              onChange={(e) => setStatusDialogPlatformFee(e.target.value)}
              helperText={t('serviceOrders.dialog.deliveryFeeHelper')}
            />
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('serviceOrders.dialog.adminNotes')}
            value={statusAdminNotes}
            onChange={(e) => setStatusAdminNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeStatusDialog}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleStatusDialogSubmit}
            disabled={
              !statusNewStatus ||
              isStatusUpdating ||
              (statusListConfirmRequiresFee && !normalizePlatformFeeInput(statusDialogPlatformFee))
            }
          >
            {isStatusUpdating ? t('serviceOrders.buttons.updating') : t('common.update')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrdersListPage;
